'use client';

import { CSSProperties, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import 'react-phone-input-material-ui/lib/style.css';
import { useTypedRouter } from '@/hooks/useTypedRouter';
import { useServerActionMutation } from '@/hooks/useServerActionMutation';
import { isValidEmail } from '@/utils/email';
import { setLoginProfileInfo } from '@/utils/sessionStorage';
import { SearchProfilePayload } from '@/services/customerProfileService.types';
import { sendOtpAction } from '@/actions/sendOtpAction';
import { Box, Button, Grid, TextField, Typography } from '@/lib/mui';
import FixedLayout from '@/components/layout/FixedLayout';
import DataRatesTNC from '@/components/DataRatesTNC';
import ScreenLoader from '@/components/ScreenLoader';
import ReactPhoneInput from '@/components/PhoneInput';
import NarvarPreferencesLogo from '@/components/NarvarPreferencesLogo';
import {
  CapImage,
  ControllerImage,
  Lipstick2Image,
  LipstickImage,
  ShoesImage,
  SpeakersImage,
} from '@/components/images/PreferencesImage';
import { AuthType, loginQueryParams } from '@/app/login/types';

const textFieldStyle: CSSProperties = {
  marginTop: 0,
  color: grey[100],
  backgroundColor: 'white',
  borderRadius: 5,
};

const inputFieldStyle: {
  style: CSSProperties;
} = {
  style: { textAlign: 'center' },
};

function Header() {
  return (
    <>
      <Box color="white" display="flex" width="100%" justifyContent="center">
        <NarvarPreferencesLogo />
      </Box>
      <Box display="flex" justifyContent="center" textAlign="center">
        <Typography
          px={{ xs: 2 }}
          py={{ xs: 3 }}
          color="#fff"
          fontSize={{ xs: 16, sm: 18 }}
          maxWidth="550px"
          fontWeight={450}
          lineHeight={'24px'}
          whiteSpace={'pre-line'}
        >
          Manage your delivery, return,{'\n'} and notification preferences
          across
          {'\n'} our network of retailers.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginLeft: '-55px',
        }}
      >
        <ControllerImage />
        <LipstickImage />
        <CapImage />
        <ControllerImage />
        <ShoesImage />
        <SpeakersImage />
        <Lipstick2Image />
        <ControllerImage />
        <LipstickImage />
        <CapImage />
        <ShoesImage />
        <SpeakersImage />
        <ControllerImage />
        <Lipstick2Image />
        <CapImage />
        <LipstickImage />
        <SpeakersImage />
        <ShoesImage />
      </Box>
    </>
  );
}

function BottomContent({
  type,
  onSwitchType,
}: {
  type: AuthType;
  onSwitchType: () => void;
}) {
  return (
    <Button
      className="__test__switch-login-mode"
      fullWidth
      variant="text"
      color="secondary"
      onClick={onSwitchType}
    >
      Switch to {type === 'email' ? 'Phone' : 'Email'}
    </Button>
  );
}

function Footer({ type }: { type: AuthType }) {
  return (
    <Grid container direction="column" alignItems="center">
      <Grid item mt={2}>
        <DataRatesTNC showRatesWarning={type === 'phone'} color={grey[500]} />
      </Grid>
    </Grid>
  );
}

export default function LoginProfileEmailOrPhone() {
  const { push } = useRouter();
  const {
    query: { type = 'email', deleteProfile },
  } = useTypedRouter(loginQueryParams);

  const [phone, setPhone] = useState('+1');
  const [email, setEmail] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const isDeleteProfileFlow = deleteProfile === 'true';

  const {
    palette: {
      primary: { main: textColor },
    },
  } = useTheme();

  const {
    mutate,
    isPending,
    error: mutationError,
  } = useServerActionMutation(sendOtpAction, {
    onSuccess: () => {
      setLoginProfileInfo({
        type,
        value: type === 'email' ? email : phone,
      });

      push(
        `/login/verify?type=${type}${isDeleteProfileFlow ? '&deleteProfile=true' : ''}`,
      );
    },
  });

  useEffect(() => {
    if (!phone || isValidPhoneNumber(phone)) {
      setPhoneError('');

      return;
    }

    setPhoneError('Please enter a valid phone number');
  }, [phone]);

  useEffect(() => {
    if (!email || isValidEmail(email)) {
      setEmailError('');

      return;
    }

    setEmailError('Please enter an email address');
  }, [email]);

  const onSave = () => {
    const body: SearchProfilePayload = {};

    if (type === 'phone') {
      body.phone_number = phone;
    } else {
      body.email = email;
    }

    mutate(body);
  };

  const error = type === 'email' ? emailError : phoneError;
  const value = type === 'email' ? email : phone;

  const handleSwitchType = () => {
    push(
      `/login?type=${type === 'email' ? 'phone' : 'email'}${isDeleteProfileFlow ? '&deleteProfile=true' : ''}`,
    );
  };

  const handleGetPassCode = () => {
    if (error || !value) return;

    onSave();
  };

  return (
    <FixedLayout
      headerContent={<Header />}
      bottomContent={
        <BottomContent type={type} onSwitchType={handleSwitchType} />
      }
      footer={<Footer type={type} />}
    >
      <ScreenLoader open={isPending} />
      <Box>
        <Typography textAlign="center" color={textColor} variant="subtitle2">
          {isDeleteProfileFlow ? (
            'Enter your email to proceed with your deletion request'
          ) : (
            <>{type === 'email' ? 'Enter Email' : 'Enter Phone Number'}</>
          )}
        </Typography>
        <Typography
          pt={2}
          textAlign="center"
          sx={{ color: (theme) => theme.palette.primary.light }}
        >
          {isDeleteProfileFlow ? (
            <>
              You will receive a one time code to verify the email associated
              with this profile and complete deletion.
            </>
          ) : (
            <>
              You will receive a one time code to verify your&nbsp;
              {type === 'email' ? 'email' : 'phone number'} for your
              preferences.
            </>
          )}
        </Typography>
        <Box pt={2}>
          {type === 'phone' ? (
            <ReactPhoneInput
              value={phone}
              onChange={(v: string) => setPhone(`+${v}`)}
              inputStyle={{
                ...textFieldStyle,
              }}
              onlyCountries={['us', 'ca']}
              dropdownStyle={{
                position: 'fixed',
                width: 'calc(100% - 4rem)',
                maxWidth: 470,
              }}
              placeholder="2222555666"
              country="us"
              isValid={!phoneError}
              label=""
              countryCodeEditable={false}
              buttonStyle={{ paddingLeft: 2, width: 20 }}
              component={TextField}
            />
          ) : (
            <TextField
              fullWidth
              type="email"
              placeholder="hello@example.com"
              style={textFieldStyle}
              inputProps={inputFieldStyle}
              error={!!emailError}
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.code === 'Enter' && handleGetPassCode) {
                  handleGetPassCode();
                }
              }}
            />
          )}
          {mutationError ? (
            <Box mt={2}>
              <Typography textAlign="center" variant="body1" color="error">
                {mutationError.message}
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Box pt={2}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleGetPassCode}
            sx={{
              borderRadius: 22,
              opacity: !error && value ? 1 : 0.7,
            }}
          >
            Get Passcode
          </Button>
        </Box>
      </Box>
    </FixedLayout>
  );
}
